/**
=========================================================
* Astro Clean Carpets - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { lazy, Suspense, useDeferredValue, useLayoutEffect } from "react";

// react-router components
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Astro Clean Carpets themes
import theme from "assets/theme";

// Astro Clean Carpets routes
import routes from "routes";

// Astro Clean Carpets components
import SeoSchema from "components/SeoSchema";
import AnalyticsTracker from "components/AnalyticsTracker";
import StickyContactWidget from "components/StickyContactWidget";

const Presentation = lazy(() => import("layouts/pages/presentation"));
const NotFound = lazy(() => import("pages/NotFound"));
const Privacy = lazy(() => import("layouts/pages/landing-pages/privacy"));

function PageLayout() {
  const { pathname } = useLocation();

  // Reset scroll only when the destination is ready, before its first paint.
  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  return (
    <>
      <SeoSchema />
      <AnalyticsTracker />
      <Outlet />
    </>
  );
}

export default function App() {
  const location = useLocation();
  // Keep the current page and its navbar mounted while a lazy destination loads.
  const displayedLocation = useDeferredValue(location);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.route} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={null}>
        <Routes location={displayedLocation}>
          <Route element={<PageLayout />}>
            {getRoutes(routes)}
            <Route path="/" element={<Presentation />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<Navigate to="/about-us" replace />} />
            <Route path="/testimonials" element={<Navigate to="/reviews" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
      <StickyContactWidget />
    </ThemeProvider>
  );
}
